import React, { useState, useEffect, useMemo } from "react";
import { 
    Card, Row, Col, Button, Spinner, Alert, Table,
    Badge, OverlayTrigger, Tooltip, Modal, ProgressBar,
    InputGroup, Form, Pagination
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";

export default function SauvegardeManuel() {
    const navigate = useNavigate();
    const [sauvegardes, setSauvegardes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [restoring, setRestoring] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState('');
    
    const [searchText, setSearchText] = useState('');

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(5);
    
    const [showBackupModal, setShowBackupModal] = useState(false);
    const [showRestoreModal, setShowRestoreModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        fetchSauvegardes();
    }, []);

    const fetchSauvegardes = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php?action=sauvegardes`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setSauvegardes(result.data || []);
            setCurrentPage(1);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleBackupConfirm = async () => {
        setShowBackupModal(false);
        setSaving(true);
        setError(null);
        setSuccess('');
        setProgress(0);
        
        const interval = setInterval(() => {
            setProgress(prev => {
                if (prev >= 90) {
                    clearInterval(interval);
                    return 90;
                }
                return prev + 10;
            });
        }, 300);
        
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        action: 'creer_sauvegarde'
                    })
                }
            );
            
            if (!response.ok) {
                const text = await response.text();
                console.error("Réponse non-OK:", response.status, text);
                throw new Error(`Erreur HTTP ${response.status}`);
            }
            
            const text = await response.text();
            let result;
            try {
                result = JSON.parse(text);
            } catch (e) {
                console.error("Erreur de parsing JSON:", e);
                throw new Error("La réponse du serveur n'est pas au format JSON valide");
            }
            
            clearInterval(interval);
            setProgress(100);
            
            if (result.success) {
                setSuccess("✅ Sauvegarde créée avec succès");
                setTimeout(() => setSuccess(''), 3000);
                fetchSauvegardes();
            } else {
                setError(result.message || "Erreur lors de la sauvegarde");
            }
        } catch (err) {
            clearInterval(interval);
            console.error("Erreur complète:", err);
            setError(err.message);
        } finally {
            setSaving(false);
            setProgress(0);
        }
    };

    const handleRestoreConfirm = async () => {
        setShowRestoreModal(false);
        setRestoring(true);
        setError(null);
        setSuccess('');
        setProgress(0);
        
        const interval = setInterval(() => {
            setProgress(prev => {
                if (prev >= 90) {
                    clearInterval(interval);
                    return 90;
                }
                return prev + 10;
            });
        }, 500);
        
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        action: 'restaurer_sauvegarde',
                        fichier: selectedFile
                    })
                }
            );
            
            const result = await response.json();
            clearInterval(interval);
            setProgress(100);
            
            if (result.success) {
                setSuccess("🔄 Restauration réussie ! La page va se recharger...");
                setTimeout(() => {
                    window.location.reload();
                }, 3000);
            } else {
                setError(result.message || "Erreur lors de la restauration");
            }
        } catch (err) {
            clearInterval(interval);
            setError(err.message);
        } finally {
            setRestoring(false);
            setProgress(0);
        }
    };

    const handleDeleteConfirm = async () => {
        setShowDeleteModal(false);
        
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php?action=delete_sauvegarde&fichier=${encodeURIComponent(selectedFile)}`,
                {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );
            
            const result = await response.json();
            
            if (result.success) {
                setSuccess("🗑️ Sauvegarde supprimée");
                fetchSauvegardes();
            } else {
                setError("Erreur lors de la suppression");
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setTimeout(() => setSuccess(''), 3000);
        }
    };

    const formatTaille = (octets) => {
        if (octets < 1024) return octets + ' o';
        if (octets < 1024 * 1024) return (octets / 1024).toFixed(1) + ' Ko';
        return (octets / (1024 * 1024)).toFixed(1) + ' Mo';
    };

    const filteredSauvegardes = useMemo(() => {
        return sauvegardes.filter(s => 
            s.nom?.toLowerCase().includes(searchText.toLowerCase()) ||
            formatDate(s.date).toLowerCase().includes(searchText.toLowerCase())
        );
    }, [sauvegardes, searchText]);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredSauvegardes.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredSauvegardes.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        let items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
            }
        }

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item 
                    key={number} 
                    active={number === currentPage}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
            }
            items.push(
                <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Chargement des sauvegardes...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* En-tête simplifié */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-database-2-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Sauvegarde manuelle</h4>
                                    <p className="text-muted small mb-0">Créer et gérer les sauvegardes de la base de données</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Alertes modernes */}
                    {success && (
                        <Alert variant="success" onClose={() => setSuccess('')} dismissible className="mb-4 alert-modern">
                            <i className="ri-checkbox-circle-line me-2 fs-5"></i>
                            {success}
                        </Alert>
                    )}
                    
                    {error && (
                        <Alert variant="danger" onClose={() => setError(null)} dismissible className="mb-4 alert-modern">
                            <i className="ri-error-warning-line me-2 fs-5"></i>
                            {error}
                        </Alert>
                    )}

                    {/* Barre de progression */}
                    {(saving || restoring) && (
                        <Card className="border-0 shadow-sm rounded-3 mb-4" style={{ background: '#f8fafc' }}>
                            <Card.Body className="p-4">
                                <div className="d-flex align-items-center gap-3 mb-3">
                                    <div className="spinner-border text-primary" style={{ width: '1.5rem', height: '1.5rem', color: '#21ACCD' }} role="status">
                                        <span className="visually-hidden">Chargement...</span>
                                    </div>
                                    <div>
                                        <strong className="d-block">
                                            {saving ? 'Sauvegarde en cours...' : 'Restauration en cours...'}
                                        </strong>
                                        <small className="text-muted">Cette opération peut prendre quelques instants</small>
                                    </div>
                                </div>
                                <div className="progress-bar-custom">
                                    <div className="progress-fill" style={{ width: `${progress}%`, background: '#21ACCD' }}></div>
                                </div>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Actions principales */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4 text-center">
                        <Card.Body className="p-5">
                            <div className="action-icon mb-3">
                                <i className="ri-database-2-line"></i>
                            </div>
                            <h4 className="mb-2 fw-bold">Créer une nouvelle sauvegarde</h4>
                            <p className="text-muted mb-4" style={{ maxWidth: '500px', margin: '0 auto' }}>
                                Cette action va sauvegarder l'intégralité de la base de données dans un fichier SQL sécurisé.
                            </p>
                            <Button 
                                variant="success" 
                                size="lg"
                                onClick={() => setShowBackupModal(true)}
                                disabled={saving || restoring}
                                className="rounded-pill px-5 py-3"
                                style={{ background: '#10b981', border: 'none' }}
                            >
                                <i className="ri-database-2-line me-2"></i>
                                Lancer la sauvegarde
                            </Button>
                        </Card.Body>
                    </Card>

                    {/* Barre de recherche */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-center g-3">
                                <Col md={8}>
                                    <div className="position-relative">
                                        <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                                        <Form.Control
                                            type="text"
                                            placeholder="Rechercher un fichier ou une date..."
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                            className="ps-5 rounded-pill"
                                        />
                                    </div>
                                </Col>
                                <Col md={4} className="text-end">
                                    <span className="badge bg-light text-dark rounded-pill px-3 py-2">
                                        <i className="ri-stack-line me-1"></i>
                                        {filteredSauvegardes.length} fichier(s)
                                    </span>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Liste des sauvegardes */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Header className="bg-transparent border-0 pt-4 pb-3">
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div className="d-flex align-items-center gap-2">
                                    <div className="section-icon-small">
                                        <i className="ri-history-line text-primary"></i>
                                    </div>
                                    <h6 className="mb-0 fw-semibold">Historique des sauvegardes</h6>
                                </div>
                                <Badge className="px-3 py-2 rounded-pill" style={{ background: '#21ACCD' }}>
                                    {filteredSauvegardes.length} fichier(s)
                                </Badge>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0 custom-table">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="ps-4">Date</th>
                                            <th style={{minWidth:'300px'}}>Fichier</th>
                                            <th>Taille</th>
                                            <th className="text-center pe-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.length > 0 ? (
                                            currentItems.map((s, index) => (
                                                <tr key={index} className="backup-row">
                                                    <td className="ps-4">
                                                        <div>
                                                            <i className="ri-calendar-line me-1 text-muted"></i>
                                                            {formatDate(s.date)}
                                                        </div>
                                                        <small className="text-muted">
                                                            <i className="ri-time-line me-1"></i>
                                                            {new Date(s.date).toLocaleTimeString('fr-FR')}
                                                        </small>
                                                    </td>
                                                    <td>
                                                        <code className="reference-badge">{s.nom}</code>
                                                    </td>
                                                    <td>
                                                        <Badge className="rounded-pill px-3" style={{ background: '#64748b' }}>
                                                            {formatTaille(s.taille)}
                                                        </Badge>
                                                    </td>
                                                    <td className="text-center pe-4">
                                                        <div className="d-flex gap-2 justify-content-center">
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>Restaurer cette sauvegarde</Tooltip>}>
                                                                <Button 
                                                                    variant="outline-warning" 
                                                                    size="sm"
                                                                    className="action-btn"
                                                                    onClick={() => {
                                                                        setSelectedFile(s.nom);
                                                                        setShowRestoreModal(true);
                                                                    }}
                                                                    disabled={restoring || saving}
                                                                >
                                                                    <i className="ri-restart-line"></i>
                                                                </Button>
                                                            </OverlayTrigger>
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>Supprimer</Tooltip>}>
                                                                <Button 
                                                                    variant="outline-danger" 
                                                                    size="sm"
                                                                    className="action-btn"
                                                                    onClick={() => {
                                                                        setSelectedFile(s.nom);
                                                                        setShowDeleteModal(true);
                                                                    }}
                                                                    disabled={restoring || saving}
                                                                >
                                                                    <i className="ri-delete-bin-line"></i>
                                                                </Button>
                                                            </OverlayTrigger>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="4" className="text-center py-5">
                                                    <div className="empty-state">
                                                        <i className="ri-inbox-line fs-48 text-muted mb-3 d-block"></i>
                                                        <h5>Aucune sauvegarde trouvée</h5>
                                                        <p className="text-muted">
                                                            {searchText 
                                                                ? "Aucun résultat ne correspond à votre recherche"
                                                                : "Créez votre première sauvegarde pour protéger vos données"}
                                                        </p>
                                                        {searchText && (
                                                            <Button 
                                                                variant="outline-primary"
                                                                onClick={() => setSearchText('')}
                                                                className="rounded-pill"
                                                            >
                                                                <i className="ri-refresh-line me-2"></i>Réinitialiser
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center p-3 border-top">
                                    <div className="text-muted small">
                                        <i className="ri-file-list-line me-1"></i>
                                        Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredSauvegardes.length)} sur {filteredSauvegardes.length} sauvegardes
                                    </div>
                                    <Pagination className="mb-0">
                                        <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                                        {renderPagination()}
                                        <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                                    </Pagination>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>

                {/* Modal de confirmation - Sauvegarde */}
                <Modal show={showBackupModal} onHide={() => setShowBackupModal(false)} centered className="modern-modal">
                    <Modal.Header closeButton className="border-0 pb-0" style={{ background: '#10b981' }}>
                        <Modal.Title className="w-100">
                            <div className="text-center">
                                <div className="confirm-icon mb-3">
                                    <i className="ri-database-2-line text-white" style={{ fontSize: '4rem' }}></i>
                                </div>
                                <h3 className="fw-bold text-white">Confirmer la sauvegarde</h3>
                            </div>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="pt-4 text-center">
                        <p className="text-muted mb-4">
                            Êtes-vous sûr de vouloir créer une sauvegarde de la base de données ?
                        </p>
                        <Alert variant="info" className="rounded-3 text-start">
                            <div className="d-flex">
                                <i className="ri-information-line fs-4 me-3 flex-shrink-0"></i>
                                <div>
                                    <strong>Bon à savoir</strong>
                                    <p className="mb-0 small mt-1">
                                        La sauvegarde sera stockée sur le serveur et pourra être restaurée ultérieurement.
                                    </p>
                                </div>
                            </div>
                        </Alert>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0 pb-4 justify-content-center">
                        <Button variant="outline-secondary" onClick={() => setShowBackupModal(false)} className="rounded-pill px-4">
                            Annuler
                        </Button>
                        <Button 
                            variant="success" 
                            onClick={handleBackupConfirm} 
                            className="rounded-pill px-5"
                            style={{ background: '#10b981', border: 'none' }}
                        >
                            <i className="ri-check-line me-2"></i>Confirmer
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Modal de confirmation - Restauration */}
                <Modal show={showRestoreModal} onHide={() => setShowRestoreModal(false)} centered className="modern-modal">
                    <Modal.Header closeButton className="border-0 pb-0" style={{ background: '#f59e0b' }}>
                        <Modal.Title className="w-100">
                            <div className="text-center">
                                <div className="confirm-icon mb-3">
                                    <i className="ri-restart-line text-white" style={{ fontSize: '4rem' }}></i>
                                </div>
                                <h3 className="fw-bold text-white">Attention</h3>
                            </div>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="pt-4 text-center">
                        <p className="text-muted mb-4">
                            Vous êtes sur le point de restaurer la sauvegarde <strong className="text-primary">"{selectedFile}"</strong>
                        </p>
                        <Alert variant="danger" className="rounded-3 text-start">
                            <div className="d-flex">
                                <i className="ri-alert-line fs-4 me-3 flex-shrink-0"></i>
                                <div>
                                    <strong>Cette action est irréversible !</strong>
                                    <p className="mb-0 small mt-1">
                                        La restauration va écraser TOUTES les données actuelles par celles de la sauvegarde.
                                    </p>
                                </div>
                            </div>
                        </Alert>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0 pb-4 justify-content-center">
                        <Button variant="outline-secondary" onClick={() => setShowRestoreModal(false)} className="rounded-pill px-4">
                            Annuler
                        </Button>
                        <Button 
                            variant="warning" 
                            onClick={handleRestoreConfirm} 
                            className="rounded-pill px-5"
                            style={{ background: '#f59e0b', border: 'none', color: 'white' }}
                        >
                            <i className="ri-restart-line me-2"></i>Restaurer
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Modal de confirmation - Suppression */}
                <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="modern-modal">
                    <Modal.Header closeButton className="border-0 pb-0" style={{ background: '#dc2626' }}>
                        <Modal.Title className="w-100">
                            <div className="text-center">
                                <div className="confirm-icon mb-3">
                                    <i className="ri-delete-bin-line text-white" style={{ fontSize: '4rem' }}></i>
                                </div>
                                <h3 className="fw-bold text-white">Supprimer la sauvegarde</h3>
                            </div>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="pt-4 text-center">
                        <p className="text-muted mb-4">
                            Êtes-vous sûr de vouloir supprimer la sauvegarde <strong className="text-primary">"{selectedFile}"</strong> ?
                        </p>
                        <Alert variant="warning" className="rounded-3 text-start">
                            <div className="d-flex">
                                <i className="ri-alert-line fs-4 me-3 flex-shrink-0"></i>
                                <div>
                                    <strong>Cette action est irréversible</strong>
                                    <p className="mb-0 small mt-1">
                                        Le fichier sera définitivement supprimé du serveur.
                                    </p>
                                </div>
                            </div>
                        </Alert>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0 pb-4 justify-content-center">
                        <Button variant="outline-secondary" onClick={() => setShowDeleteModal(false)} className="rounded-pill px-4">
                            Annuler
                        </Button>
                        <Button 
                            variant="danger" 
                            onClick={handleDeleteConfirm} 
                            className="rounded-pill px-5"
                        >
                            <i className="ri-delete-bin-line me-2"></i>Supprimer
                        </Button>
                    </Modal.Footer>
                </Modal>

                <Footer />
            </div>

            <style>{`
                /* Action Icon */
                .action-icon {
                    width: 80px;
                    height: 80px;
                    background: linear-gradient(135deg, #10b98120, #05966920);
                    border-radius: 50%;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto;
                }
                .action-icon i {
                    font-size: 2.5rem;
                    color: #10b981;
                }
                
                /* Section Icon Small */
                .section-icon-small {
                    width: 32px;
                    height: 32px;
                    background: linear-gradient(135deg, #21ACCD20, #1a8ba820);
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                
                /* Reference Badge */
                .reference-badge {
                    background: #f1f5f9;
                    padding: 0.2rem 0.6rem;
                    border-radius: 6px;
                    font-size: 0.7rem;
                    font-family: monospace;
                    display: inline-block;
                }
                
                /* Action Buttons */
                .action-btn {
                    width: 32px;
                    height: 32px;
                    border-radius: 8px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                    border: 1px solid #e2e8f0;
                    background: white;
                    color: #64748b;
                }
                .action-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                .btn-outline-warning:hover {
                    background: #f59e0b;
                    border-color: #f59e0b;
                    color: white;
                }
                .btn-outline-danger:hover {
                    background: #ef4444;
                    border-color: #ef4444;
                    color: white;
                }
                
                /* Progress Bar */
                .progress-bar-custom {
                    background: #e2e8f0;
                    border-radius: 4px;
                    height: 6px;
                    overflow: hidden;
                }
                .progress-fill {
                    height: 100%;
                    border-radius: 4px;
                    transition: width 0.3s ease;
                }
                
                /* Custom Table */
                .custom-table thead th {
                    background: #f8fafc;
                    color: #1e293b;
                    font-weight: 600;
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 1rem 0.75rem;
                    border-bottom: 2px solid #e2e8f0;
                }
                
                .custom-table tbody td {
                    padding: 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                }
                
                .backup-row {
                    transition: all 0.2s ease;
                }
                
                .backup-row:hover {
                    background: #f8fafc;
                }
                
                /* Pagination */
                .pagination {
                    gap: 5px;
                }
                .pagination .page-item .page-link {
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    color: #1e293b;
                    padding: 0.5rem 1rem;
                    transition: all 0.2s ease;
                    font-weight: 500;
                }
                .pagination .page-item.active .page-link {
                    background: linear-gradient(135deg, #21ACCD, #1a8ba8);
                    border-color: #21ACCD;
                    color: white;
                    box-shadow: 0 4px 10px rgba(33, 172, 205, 0.3);
                }
                .pagination .page-item .page-link:hover {
                    background: #f1f5f9;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                .pagination .page-item.disabled .page-link {
                    opacity: 0.5;
                    pointer-events: none;
                }
                
                /* Modern Modal */
                .modern-modal .modal-content {
                    border-radius: 20px;
                    border: none;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.2);
                    overflow: hidden;
                }
                .confirm-icon {
                    animation: scaleIn 0.5s ease-out;
                }
                @keyframes scaleIn {
                    0% { transform: scale(0); opacity: 0; }
                    50% { transform: scale(1.2); }
                    100% { transform: scale(1); opacity: 1; }
                }
                
                .alert-modern {
                    border: none;
                    border-radius: 12px;
                    padding: 1rem 1.5rem;
                    box-shadow: 0 4px 8px rgba(0,0,0,0.05);
                }
                
                .rounded-3 {
                    border-radius: 12px !important;
                }
                
                .rounded-pill {
                    border-radius: 50px !important;
                }
                
                .fs-20 {
                    font-size: 1.25rem;
                }
                .fs-48 {
                    font-size: 3rem;
                }
            `}</style>
        </>
    );
}